import { Box, Button, Chip, Paper, Tooltip, Typography } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import React, { useEffect, useRef, useState } from "react";

const BRAND_COLOR = "#2E7C67";

const SelectableTags = ({
  Title,
  Options = [] as any[],
  KeyName = "name",
  onSelectUpdate,
}) => {
  const [selectedTags, setSelectedTags] = useState<any>([]);
  const hasInitialized = useRef(false);
  const onSelectUpdateRef = useRef(onSelectUpdate);

  useEffect(() => {
    onSelectUpdateRef.current = onSelectUpdate;
  });

  // Apply defaults only once on first load when Options become available
  useEffect(() => {
    if (!Options?.length || hasInitialized.current) return;

    hasInitialized.current = true;

    const defaultSelected = Options.filter((element: any) =>
      Boolean(element.default_include)
    );

    if (defaultSelected.length === 0) return;

    const selectedIds = defaultSelected.map((element: any) => element.id);
    setSelectedTags(selectedIds);
    onSelectUpdateRef.current(defaultSelected);
  }, [Options]);

  const allSelected =
    Options.length > 0 && selectedTags.length === Options.length;

  const defaultOptions: any[] = Options.filter((element: any) =>
    Boolean(element.default_include)
  );
  const defaultIds: any[] = defaultOptions.map((element: any) => element.id);

  const handleSelection = (value: any) => {
    const selection = selectedTags.includes(value)
      ? selectedTags.filter((element: any) => element !== value)
      : [...selectedTags, value];

    setSelectedTags(selection);

    const outSelection = Options.filter((element: any) =>
      selection.includes(element.id)
    );
    onSelectUpdate(outSelection);
  };

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedTags([]);
      onSelectUpdate([]);
    } else {
      setSelectedTags(Options.map((element: any) => element.id));
      onSelectUpdate(Options);
    }
  };

  const handleResetToDefaults = () => {
    setSelectedTags(defaultIds);
    onSelectUpdate(defaultOptions);
  };

  const buttonSx = {
    textTransform: "none",
    color: BRAND_COLOR,
    borderColor: BRAND_COLOR,
    fontSize: "12px",
    padding: "1px 8px",
    minWidth: "unset",
    lineHeight: 1.5,
    ml: "0.75rem",
    "&:hover": {
      backgroundColor: `${BRAND_COLOR}1A`,
      borderColor: BRAND_COLOR,
    },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        margin: "5px",
        padding: "5px",
        backgroundColor: "#F4F2F2",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          p: "4px",
        }}
      >
        <Typography fontSize={"14px"} fontWeight="bold">
          {Title}
        </Typography>
        {Options.length > 0 && (
          <>
            <Button size="small" variant="outlined" onClick={handleToggleAll} sx={buttonSx}>
              {allSelected ? "Deselect All" : "Select All"}
            </Button>
            <Tooltip title="Reset to defaults" placement="top">
              <Button
                size="small"
                variant="text"
                onClick={handleResetToDefaults}
                sx={{
                  ml: "0.75rem",
                  minWidth: "unset",
                  padding: "1px 4px",
                  color: "text.secondary",
                  "&:hover": {
                    backgroundColor: `${BRAND_COLOR}1A`,
                    color: BRAND_COLOR,
                  },
                }}
              >
                <RestartAltIcon sx={{ fontSize: "18px" }} />
              </Button>
            </Tooltip>
          </>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
        }}
      >
        {Options.map((element, index) => (
          <Box
            key={index}
            sx={{
              p: "4px",
            }}
          >
            <Chip
              key={index}
              label={element[KeyName]}
              variant="outlined"
              sx={{
                color: selectedTags.includes(element.id)
                  ? "#FFFFFF"
                  : BRAND_COLOR,
                backgroundColor: selectedTags.includes(element.id)
                  ? BRAND_COLOR
                  : "transparent",
                borderRadius: "5px",
                "&:hover, &.MuiChip-root:hover": {
                  backgroundColor: `${BRAND_COLOR}33 !important`,
                },
              }}
              onClick={() => handleSelection(element.id)}
            />
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default SelectableTags;
